import { AppIcon } from '@/components/ui/app-icon';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { iconArrowUp, iconCircleCheck, iconSpinnerThird } from '@/lib/icons';
import { cn } from '@/lib/utils';
import {
  useGetMyProductHuntCreditsQuery,
  useSubmitProductHuntCreditMutation,
  type ProductHuntCreditRequest,
  type ProductHuntRewardKind,
} from '@/store/features/producthunt-credits.feature';
import { useState } from 'react';
import { toast } from 'sonner';

/** Reward amounts, kept in sync with PRODUCTHUNT_*_CREDITS on the backend
 *  (cleanse_api/core/config.py). Display-only. */
export const PRODUCTHUNT_UPVOTE_CREDITS = 500;

/** Our Product Hunt launch. TODO: replace with the real launch post URL once
 *  we go live (this products page is a safe fallback until then). */
export const PRODUCTHUNT_LAUNCH_URL =
  'https://www.producthunt.com/products/cleanlist-ai';

/** Loose client-side check mirroring is_valid_producthunt_url on the BE.
 *  The server is authoritative — this only gives instant feedback. */
function looksLikeProductHuntUrl(url: string): boolean {
  try {
    const u = new URL(url.trim());
    const host = u.hostname.toLowerCase();
    return (
      (u.protocol === 'https:' || u.protocol === 'http:') &&
      (host === 'producthunt.com' || host === 'www.producthunt.com')
    );
  } catch {
    return false;
  }
}

interface ClaimRowProps {
  kind: ProductHuntRewardKind;
  credits: number;
  title: string;
  hint: string;
  placeholder: string;
  icon: typeof iconArrowUp;
  existing: ProductHuntCreditRequest | null;
}

function ClaimRow({
  kind,
  credits,
  title,
  hint,
  placeholder,
  icon,
  existing,
}: ClaimRowProps) {
  const [url, setUrl] = useState('');
  const [submit, { isLoading }] = useSubmitProductHuntCreditMutation();

  const trimmed = url.trim();
  const valid = looksLikeProductHuntUrl(trimmed);
  const approved = existing?.status === 'approved';
  const pending = existing?.status === 'pending';

  const handleSubmit = async () => {
    if (!valid) {
      toast.error('Enter a valid Product Hunt link');
      return;
    }
    try {
      await submit({ reward_kind: kind, evidence_url: trimmed }).unwrap();
      toast.success(
        'Submitted! Our team will verify it and add your credits shortly.'
      );
      setUrl('');
    } catch (err) {
      const detail = (err as { data?: { detail?: string } })?.data?.detail;
      toast.error(
        typeof detail === 'string' ? detail : 'Could not submit your request'
      );
    }
  };

  return (
    <div className='rounded-lg border border-border bg-muted/20 p-3'>
      <div className='flex items-center gap-2'>
        <AppIcon
          icon={icon}
          className='text-orange-500'
          style={{ fontSize: 14 }}
        />
        <span className='text-sm font-semibold text-foreground'>{title}</span>
        <span className='ml-auto rounded-full bg-orange-500/10 px-2 py-0.5 text-[11px] font-bold text-orange-600 dark:text-orange-400'>
          +{credits.toLocaleString()} credits
        </span>
      </div>

      {approved ? (
        <p className='mt-2 flex items-center gap-1.5 text-[13px] font-medium text-emerald-600 dark:text-emerald-400'>
          <AppIcon icon={iconCircleCheck} style={{ fontSize: 13 }} />
          {credits.toLocaleString()} credits added — thank you!
        </p>
      ) : pending ? (
        <p className='mt-2 flex items-center gap-1.5 text-[13px] text-muted-foreground'>
          <AppIcon
            icon={iconSpinnerThird}
            className='animate-spin'
            style={{ fontSize: 12 }}
          />
          Submitted — awaiting verification.
        </p>
      ) : (
        <>
          <p className='mt-1 text-[12px] text-muted-foreground'>{hint}</p>
          <div className='mt-2 flex items-center gap-2'>
            <Input
              type='url'
              inputMode='url'
              placeholder={placeholder}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && valid && !isLoading) handleSubmit();
              }}
              className='h-8 text-xs'
            />
            <Button
              size='sm'
              variant='brand'
              className='h-8 shrink-0 bg-orange-500 text-xs text-white hover:bg-orange-500/90'
              onClick={handleSubmit}
              disabled={!valid || isLoading}
            >
              {isLoading ? (
                <AppIcon
                  icon={iconSpinnerThird}
                  className='animate-spin'
                  style={{ fontSize: 13 }}
                />
              ) : (
                'Submit'
              )}
            </Button>
          </div>
          {trimmed.length > 0 && !valid && (
            <p className='mt-1 text-[11px] text-orange-600 dark:text-orange-400'>
              That doesn't look like a producthunt.com link.
            </p>
          )}
        </>
      )}
    </div>
  );
}

interface ProductHuntRewardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ProductHuntRewardDialog({
  open,
  onOpenChange,
}: ProductHuntRewardDialogProps) {
  const { data } = useGetMyProductHuntCreditsQuery(undefined, { skip: !open });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <AppIcon
              icon={iconArrowUp}
              className='text-orange-500'
              style={{ fontSize: 16 }}
            />
            Support our Product Hunt launch
          </DialogTitle>
          <DialogDescription>
            Earn {PRODUCTHUNT_UPVOTE_CREDITS.toLocaleString()} credits. Upvote
            us on{' '}
            <a
              href={PRODUCTHUNT_LAUNCH_URL}
              target='_blank'
              rel='noopener noreferrer'
              className={cn(
                'font-medium text-orange-600 hover:underline dark:text-orange-400'
              )}
            >
              Product Hunt
            </a>
            , then paste the link below. Our team verifies it — one reward per
            workspace.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-2 py-1'>
          <ClaimRow
            kind='upvote'
            credits={PRODUCTHUNT_UPVOTE_CREDITS}
            title='Upvote the launch'
            hint='Paste your Product Hunt profile URL so we can confirm your upvote.'
            placeholder='https://www.producthunt.com/@your-username'
            icon={iconArrowUp}
            existing={data?.upvote ?? null}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
